import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'

import type {
    BrowseFilters,
    BrowseResult,
    Category,
    ColorCount
} from '@/types'

import Link from 'next/link'
import { RotateCcw, SearchX, X } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { EmptyState } from '@/components/generals/empty-state'
import { BrowseFilterBar } from '@/components/wallpapers/browse-filters'
import { WallpaperMasonry } from '@/components/wallpapers/wallpaper-masonry'
import { Pagination } from '@/components/wallpapers/pagination'
import { buttonVariants } from '@/components/ui/button'
import { useRouteLoading } from '@/hooks/use-route-loading'
import { cn } from '@/lib/utils'
import { getColorFamilyInfo } from '@/lib/colors'
import { pluralize } from '@/lib/format'
import { browseWallpapers, getCategories, getLibraryOverview } from '@/lib/wallpapers'

import {
    buildBrowseHref,
    DEFAULT_FILTERS,
    hasActiveFilters,
    parseBrowseFilters
} from '@/lib/browse'

interface BrowseProps {
    filters: BrowseFilters
    result: BrowseResult
    categories: Array<Category>
    colors: Array<ColorCount>
    error: boolean
}

interface ActiveChipProps {
    href: string
    children: ReactNode
}

const ActiveChip: FC<ActiveChipProps> = ({ href, children }): ReactNode => {
    return (
        <Link
            href={href}
            scroll={false}
            className='inline-flex h-8 items-center gap-1.5 rounded-full bg-white/[0.08] pr-2 pl-3 text-sm text-foreground transition hover:bg-white/[0.14] focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
        >
            <span className='sr-only'>Remove filter</span>
            {children}
            <X aria-hidden='true' className='size-3.5 text-muted-foreground' />
        </Link>
    )
}

const Browse: FC<BrowseProps> = ({ filters, result, categories, colors, error }): ReactNode => {
    const loading = useRouteLoading()
    const category = categories.find(item => item.slug === filters.category) ?? null
    const color = getColorFamilyInfo(filters.color)
    const deviceLabel = filters.device === 'phone' ? 'Phone' : filters.device === 'desktop' ? 'Desktop' : null
    const active = hasActiveFilters(filters)

    const pageName = filters.q
        ? `Search results for ${filters.q}`
        : category
            ? `${category.name} wallpapers`
            : color
                ? `${color.label} wallpapers`
                : deviceLabel
                    ? `${deviceLabel} wallpapers`
                    : 'Browse wallpapers'

    const seoTitle = result.page > 1 ? `${pageName}, page ${result.page}` : pageName

    return (
        <Layout>
            <Seo
                title={seoTitle}
                description={category?.description || 'Browse free desktop and phone wallpapers by category, device and color, then download the full-size file in one click.'}
            />

            <section id='browse-header' className='pt-10 sm:pt-14'>
                <Container>
                    <p className='text-xs font-semibold tracking-[0.16em] text-brand uppercase'>
                        {filters.q ? 'Search' : category ? 'Category' : 'Browse'}
                    </p>

                    <h1 className='mt-3 font-heading text-5xl leading-[1.02] tracking-tight sm:text-6xl'>
                        {filters.q ? (
                            <>
                                Results for
                                {' '}
                                <em className='text-brand'>“{filters.q}”</em>
                            </>
                        ) : category ? (
                            <span data-mdlfy-cms={`categories:${category.id}:name`}>
                                {category.name}
                            </span>
                        ) : color ? (
                            <>
                                {color.label}
                                {' wallpapers'}
                            </>
                        ) : deviceLabel ? (
                            <>
                                {deviceLabel}
                                {' wallpapers'}
                            </>
                        ) : (
                            'All wallpapers'
                        )}
                    </h1>

                    {!filters.q && category?.description ? (
                        <p
                            className='mt-4 max-w-2xl text-[0.95rem] leading-relaxed text-muted-foreground'
                            data-mdlfy-cms={`categories:${category.id}:description`}
                        >
                            {category.description}
                        </p>
                    ) : (
                        <p className='mt-4 max-w-2xl text-[0.95rem] leading-relaxed text-muted-foreground'>
                            Hand-picked backgrounds for every screen. Filter by category, device or color, then download the full-size file.
                        </p>
                    )}
                </Container>
            </section>

            <section id='filters' aria-label='Filters' className='pt-8 sm:pt-10'>
                <Container>
                    <BrowseFilterBar filters={filters} categories={categories} colors={colors} />
                </Container>
            </section>

            <section id='results' aria-labelledby='results-title' className='pt-8'>
                <Container>
                    <h2 id='results-title' className='sr-only'>
                        Wallpapers
                    </h2>

                    <div className='flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.06] pt-6'>
                        <p className='text-sm text-muted-foreground' aria-live='polite'>
                            {error ? 'Wallpapers could not be loaded.' : (
                                <>
                                    <span className='font-medium text-foreground'>{result.total}</span>
                                    {' '}
                                    {pluralize(result.total, 'wallpaper')}
                                    {result.pageCount > 1 && (
                                        <>
                                            {', page '}
                                            <span>{result.page}</span>
                                            {' of '}
                                            <span>{result.pageCount}</span>
                                        </>
                                    )}
                                </>
                            )}
                        </p>

                        {active && (
                            <div className='flex flex-wrap items-center gap-2'>
                                {filters.q && (
                                    <ActiveChip href={buildBrowseHref(filters, { q: '' })}>
                                        “{filters.q}”
                                    </ActiveChip>
                                )}

                                {filters.category && (
                                    <ActiveChip href={buildBrowseHref(filters, { category: '' })}>
                                        {category ? (
                                            <span data-mdlfy-cms={`categories:${category.id}:name`}>
                                                {category.name}
                                            </span>
                                        ) : filters.category}
                                    </ActiveChip>
                                )}

                                {deviceLabel && (
                                    <ActiveChip href={buildBrowseHref(filters, { device: 'all' })}>
                                        {deviceLabel}
                                    </ActiveChip>
                                )}

                                {color && (
                                    <ActiveChip href={buildBrowseHref(filters, { color: '' })}>
                                        <span aria-hidden='true' className='size-3 rounded-full ring-1 ring-white/20' style={{ backgroundColor: color.swatch }} />
                                        {color.label}
                                    </ActiveChip>
                                )}

                                <Link
                                    href={buildBrowseHref(DEFAULT_FILTERS, { sort: filters.sort })}
                                    scroll={false}
                                    className='ml-1 rounded text-sm font-medium text-foreground/80 underline-offset-4 hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
                                >
                                    Clear all
                                </Link>
                            </div>
                        )}
                    </div>

                    <div aria-busy={loading} className={cn('mt-6 transition-opacity duration-200', loading && 'pointer-events-none opacity-50')}>
                        {error ? (
                            <EmptyState
                                icon={<RotateCcw aria-hidden='true' className='size-5' />}
                                title="We couldn't load wallpapers right now"
                                description='This is usually over in a moment. Try again, or come back shortly.'
                                action={(
                                    <Link href={buildBrowseHref(filters, { page: filters.page })} className={buttonVariants({ size: 'lg' })}>
                                        Try again
                                    </Link>
                                )}
                            />
                        ) : result.wallpapers.length === 0 ? (
                            <EmptyState
                                icon={<SearchX aria-hidden='true' className='size-5' />}
                                title={result.total > 0 ? 'This page is empty' : 'No wallpapers match'}
                                description={result.total > 0 ? 'There are fewer pages than that. Head back to the first one.' : 'Try a different search, or remove a filter to see more.'}
                                action={result.total > 0 ? (
                                    <Link href={buildBrowseHref(filters, { page: 1 })} className={buttonVariants({ size: 'lg' })}>
                                        Back to page one
                                    </Link>
                                ) : active ? (
                                    <Link href={buildBrowseHref(DEFAULT_FILTERS, { sort: filters.sort })} className={buttonVariants({ size: 'lg' })}>
                                        Clear filters
                                    </Link>
                                ) : undefined}
                            />
                        ) : (
                            <WallpaperMasonry wallpapers={result.wallpapers} preloadFirst />
                        )}
                    </div>

                    {!error && result.wallpapers.length > 0 && (
                        <div className='mt-12'>
                            <Pagination
                                page={result.page}
                                pageCount={result.pageCount}
                                hrefFor={page => buildBrowseHref(filters, { page })}
                            />
                        </div>
                    )}
                </Container>
            </section>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<BrowseProps> = async ({ query }) => {
    const filters = parseBrowseFilters(query)

    try {
        const [result, categories, overview] = await Promise.all([
            browseWallpapers(filters),
            getCategories(),
            getLibraryOverview(0)
        ])

        return {
            props: {
                filters,
                result,
                categories,
                colors: overview.colors,
                error: false
            }
        }
    }
    catch (error) {
        console.error('The browse page could not load wallpapers.', error)

        return {
            props: {
                filters,
                result: { wallpapers: [], total: 0, page: filters.page, pageCount: 1 },
                categories: [],
                colors: [],
                error: true
            }
        }
    }
}

export default Browse
